/**
 * Game Playground Page
 * 
 * Phase 6.0 Track C: Local testing page for Wheel of Fortune
 * No Shopify/OAuth required
 */

import { useState, useEffect, useRef } from "react";
import {
  Page,
  Card,
  BlockStack,
  InlineStack,
  Button,
  Checkbox,
  Text,
  Banner,
  Layout,
  Collapsible,
} from "@shopify/polaris";
import { PixiApp } from "../games/runtime/PixiApp";
import { WheelGame } from "../games/wheel_of_fortune/WheelGame";
import { loadGameManifest } from "../games/runtime/AssetLoader";
import { WheelConfigEditor } from "../components/WheelConfigEditor";
import type { GameOutcome, GameState, WheelConfig, DebugOptions, GameManifestV1 } from "../games/runtime/types";

function GamePlaygroundPage() {
  const canvasRef = useRef<HTMLDivElement>(null);
  const pixiAppRef = useRef<PixiApp | null>(null);
  const wheelGameRef = useRef<WheelGame | null>(null);
  const animationFrameRef = useRef<number | null>(null);

  const [gameState, setGameState] = useState<GameState>("idle");
  const [lastOutcome, setLastOutcome] = useState<GameOutcome | null>(null);
  const [manifest, setManifest] = useState<GameManifestV1 | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [debug, setDebug] = useState<DebugOptions>({
    showSegmentIndices: false,
    showStopAngle: false,
    logOutcome: true,
  });
  const [debugPanelOpen, setDebugPanelOpen] = useState(false);

  // Wheel config state (editable)
  const [wheelConfig, setWheelConfig] = useState<WheelConfig>({
    segments: [
      { outcomeKey: "discount_10", label: "10% Off", color: "#FF6B6B", weight: 2 },
      { outcomeKey: "discount_20", label: "20% Off", color: "#4ECDC4", weight: 1 },
      { outcomeKey: "free_shipping", label: "Free Ship", color: "#FFE66D", weight: 2 },
      { outcomeKey: "try_again", label: "Try Again", color: "#95E1D3", weight: 3 },
      { outcomeKey: "discount_50", label: "50% Off!", color: "#FF6B9D", weight: 1 },
      { outcomeKey: "bonus_points", label: "100 Points", color: "#A8E6CF", weight: 2 },
    ],
    spinDuration: 4000,
    theme: "classic",
    ctaText: "Spin to Win!",
  });

  // Update config from manifest when loaded
  useEffect(() => {
    if (manifest?.defaultConfig) {
      setWheelConfig(manifest.defaultConfig as unknown as WheelConfig);
    }
  }, [manifest]);

  /**
   * Load game manifest
   */
  useEffect(() => {
    const loadManifest = async () => {
      try {
        setGameState("loading");
        const loadedManifest = await loadGameManifest("wheel_of_fortune", "v1");
        setManifest(loadedManifest);
        console.log("[Playground] Manifest loaded:", loadedManifest);
      } catch (error) {
        console.error("[Playground] Failed to load manifest:", error);
        setLoadError(error instanceof Error ? error.message : "Failed to load manifest");
        // Continue with fallback config
        setManifest(null);
      }
    };

    loadManifest();
  }, []);

  /**
   * Initialize Pixi and Wheel Game (once on mount)
   */
  useEffect(() => {
    const initGame = async () => {
      if (!canvasRef.current || !wheelConfig) return;

      try {
        setGameState("loading");
        
        // Create Pixi app
        const pixiApp = new PixiApp();
        await pixiApp.init(canvasRef.current, {
          width: 600,
          height: 600,
          backgroundColor: 0x1a1a2e,
          antialias: true,
        });
        pixiAppRef.current = pixiApp;

        // Create wheel game
        const wheelGame = new WheelGame({
          config: wheelConfig,
          width: 600,
          height: 600,
          debug,
          onStateChange: (state) => {
            console.log("[Playground] State changed:", state);
            setGameState(state);
          },
          onOutcome: (outcome) => {
            console.log("[Playground] Outcome received:", outcome);
            setLastOutcome(outcome);
          },
        });
        wheelGameRef.current = wheelGame;

        const stage = pixiApp.getStage();
        if (stage) {
          stage.addChild(wheelGame);
        }

        // Start game loop
        const gameLoop = () => {
          if (wheelGame) {
            wheelGame.update(1 / 60); // Assuming 60fps
          }
          animationFrameRef.current = requestAnimationFrame(gameLoop);
        };
        gameLoop();

        setGameState("ready");
        console.log("[Playground] Game initialized");
      } catch (error) {
        console.error("[Playground] Init error:", error);
        setGameState("error");
      }
    };

    initGame();

    // Cleanup
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (wheelGameRef.current) {
        // Don't destroy, just cleanup refs
        wheelGameRef.current = null;
      }
      if (pixiAppRef.current) {
        pixiAppRef.current.destroy();
        pixiAppRef.current = null;
      }
    };
  }, []); // Initialize only once on mount

  /**
   * Update debug options when they change
   */
  useEffect(() => {
    if (wheelGameRef.current) {
      wheelGameRef.current.updateDebug(debug);
    }
  }, [debug]);

  /**
   * Log config changes for debugging
   */
  useEffect(() => {
    console.log("[Playground] Config updated:", wheelConfig);
  }, [wheelConfig]);

  /**
   * Handle spin button click
   */
  const handleSpin = () => {
    if (gameState !== "ready") {
      console.warn("[Playground] Cannot spin in state:", gameState);
      return;
    }

    // Mock server outcome (random segment selection for testing)
    const randomSegmentIndex = Math.floor(Math.random() * wheelConfig.segments.length);
    const selectedSegment = wheelConfig.segments[randomSegmentIndex];

    // Calculate angles
    const totalWeight = wheelConfig.segments.reduce((sum, seg) => sum + seg.weight, 0);
    let cumulativeAngle = 0;
    let targetAngle = 0;

    for (let i = 0; i < wheelConfig.segments.length; i++) {
      const segment = wheelConfig.segments[i];
      const segmentAngle = (segment.weight / totalWeight) * 360;

      if (i === randomSegmentIndex) {
        targetAngle = cumulativeAngle + segmentAngle / 2;
        break;
      }

      cumulativeAngle += segmentAngle;
    }

    const baseRotations = 5;
    const totalRotation = baseRotations * 360 + targetAngle;

    const mockOutcome: GameOutcome = {
      outcomeKey: selectedSegment.outcomeKey,
      targetAngle,
      totalRotation,
      segmentLabel: selectedSegment.label,
      segmentColor: selectedSegment.color,
    };

    console.log("[Playground] Spinning with mock outcome:", mockOutcome);

    if (wheelGameRef.current) {
      wheelGameRef.current.spin(mockOutcome);
    }
  };

  /**
   * Reset game
   */
  const handleReset = () => {
    if (wheelGameRef.current) {
      wheelGameRef.current.reset();
    }
    setLastOutcome(null);
  };

  // Handle config changes
  const handleConfigChange = (newConfig: WheelConfig) => {
    setWheelConfig(newConfig);
    if (wheelGameRef.current) {
      wheelGameRef.current.updateConfig(newConfig);
    }
  };

  return (
    <Page
      title="Game Playground"
      subtitle="Local testing for Wheel of Fortune (no Shopify required)"
      backAction={{ content: "Dashboard", url: "/" }}
    >
      <BlockStack gap="400">
        <Banner tone="info">
          <Text as="p">
            This is a local testing environment. No Shopify OAuth or CDN required.
          </Text>
        </Banner>

        {loadError && (
          <Banner tone="warning">
            <Text as="p">
              Failed to load manifest from server: {loadError}. Using fallback config.
            </Text>
          </Banner>
        )}

        {manifest && (
          <Banner tone="success">
            <Text as="p">
              Manifest loaded: {manifest.gameKey} v{manifest.gameVersion}
            </Text>
          </Banner>
        )}

        <Layout>
          <Layout.Section variant="oneThird">
            {/* Config Editor */}
            <WheelConfigEditor config={wheelConfig} onChange={handleConfigChange} />
          </Layout.Section>

          <Layout.Section>
            <BlockStack gap="400">
              {/* Game Canvas */}
              <Card>
            <BlockStack gap="400">
              <Text as="h2" variant="headingMd">
                Wheel of Fortune
              </Text>

              <div
                ref={canvasRef}
                style={{
                  width: "600px",
                  height: "600px",
                  border: "2px solid #e1e3e5",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              />

              <InlineStack gap="200">
                <Button
                  onClick={handleSpin}
                  disabled={gameState !== "ready"}
                  variant="primary"
                >
                  {gameState === "playing" || gameState === "resolving"
                    ? "Spinning..."
                    : "Spin to Win!"}
                </Button>

                <Button onClick={handleReset} disabled={gameState === "idle"}>
                  Reset
                </Button>
              </InlineStack>

              <Text as="p" tone="subdued">
                State: <strong>{gameState}</strong>
              </Text>
            </BlockStack>
          </Card>

          {/* Debug Panel (Collapsible) */}
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between" blockAlign="center">
                <Text as="h2" variant="headingMd">
                  Debug Tools
                </Text>
                <Button
                  onClick={() => setDebugPanelOpen(!debugPanelOpen)}
                  disclosure={debugPanelOpen ? 'up' : 'down'}
                  size="slim"
                >
                  {debugPanelOpen ? 'Hide' : 'Show'}
                </Button>
              </InlineStack>

              <Collapsible
                open={debugPanelOpen}
                id="debug-panel"
                transition={{ duration: '200ms', timingFunction: 'ease-in-out' }}
              >
                <BlockStack gap="400">
                  <Checkbox
                    label="Show Segment Indices"
                    checked={debug.showSegmentIndices || false}
                    onChange={(checked) =>
                      setDebug({ ...debug, showSegmentIndices: checked })
                    }
                  />

                  <Checkbox
                    label="Show Stop Angle"
                    checked={debug.showStopAngle || false}
                    onChange={(checked) =>
                      setDebug({ ...debug, showStopAngle: checked })
                    }
                  />

                  <Checkbox
                    label="Log Outcome to Console"
                    checked={debug.logOutcome || false}
                    onChange={(checked) =>
                      setDebug({ ...debug, logOutcome: checked })
                    }
                  />

                  {lastOutcome && (
                    <BlockStack gap="200">
                      <Text as="h3" variant="headingSm">
                        Last Outcome
                      </Text>
                      <Text as="p" variant="bodySm">
                        <strong>Key:</strong> {lastOutcome.outcomeKey}
                      </Text>
                      <Text as="p" variant="bodySm">
                        <strong>Label:</strong> {lastOutcome.segmentLabel}
                      </Text>
                      <Text as="p" variant="bodySm">
                        <strong>Target Angle:</strong> {lastOutcome.targetAngle?.toFixed(2)}°
                      </Text>
                      <Text as="p" variant="bodySm">
                        <strong>Total Rotation:</strong> {lastOutcome.totalRotation?.toFixed(2)}°
                      </Text>
                    </BlockStack>
                  )}
                </BlockStack>
              </Collapsible>
            </BlockStack>
          </Card>
            </BlockStack>
          </Layout.Section>
        </Layout>
      </BlockStack>
    </Page>
  );
}

export default GamePlaygroundPage;
